import React, { useState, useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { FiBookOpen, FiFileText, FiEye, FiDownload, FiTrendingUp, FiCalendar } from "react-icons/fi";
import { Spinner } from "react-bootstrap";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const Dashboard = () => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalBlogs: 0,
    totalMagazines: 0,
    totalBlogViews: 0,
    totalMagazineViews: 0,
    totalDownloads: 0,
    newInPeriod: 0,
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [topDownloadedMagazine, setTopDownloadedMagazine] = useState(null);
  const [topViewedMagazine, setTopViewedMagazine] = useState(null);
  const [topBlog, setTopBlog] = useState(null);

  // Chart data
  const [contentChart, setContentChart] = useState([]);
  const [performanceChart, setPerformanceChart] = useState([]);
  const [monthlyChart, setMonthlyChart] = useState([]);
  const [topMagazinesChart, setTopMagazinesChart] = useState([]);

  // Filter states
  const [filterType, setFilterType] = useState("thisMonth"); // "thisMonth" | "lastMonth" | "last30" | "custom" | "all"
  const [customMonth, setCustomMonth] = useState(new Date());
  const [customRange, setCustomRange] = useState([null, null]);
  const [startDate, endDate] = customRange;

  const getQueryParams = () => {
    const params = new URLSearchParams();

    if (filterType === "thisMonth" || filterType === "lastMonth") {
      const targetDate = filterType === "thisMonth" ? new Date() : new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
      const year = targetDate.getFullYear();
      const month = targetDate.getMonth() + 1;
      params.append("month", `${year}-${month.toString().padStart(2, "0")}`);
    }
    else if (filterType === "last30") {
      const from = new Date();
      from.setDate(from.getDate() - 30);
      params.append("start", from.toISOString().split("T")[0]);
      params.append("end", new Date().toISOString().split("T")[0]);
    }
    else if (filterType === "custom" && startDate && endDate) {
      params.append("start", startDate.toISOString().split("T")[0]);
      params.append("end", endDate.toISOString().split("T")[0]);
    }
    // "all" → no date params

    return params;
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    const token = localStorage.getItem("MagazineToken");

    if (!token) {
      console.warn("No token → using fallback");
      loadFallbackData();
      setLoading(false);
      return;
    }

    const query = getQueryParams();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard-stats${query.toString() ? `?${query}` : ""}`;

    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      setStats({
        totalBlogs: data.totalBlogs ?? 0,
        totalMagazines: data.totalMagazines ?? 0,
        totalBlogViews: data.totalBlogViews ?? 0,
        totalMagazineViews: data.totalMagazineViews ?? 0,
        totalDownloads: data.totalDownloads ?? 0,
        newInPeriod: data.newInPeriod ?? data.newThisMonth ?? 0,
      });

      setRecentActivity(data.recentActivity ?? []);
      setTopDownloadedMagazine(data.topDownloadedMagazine ?? null);
      setTopViewedMagazine(data.topViewedMagazine ?? null);
      setTopBlog(data.topBlog ?? null);

      setContentChart(data.contentChart ?? []);
      setPerformanceChart(data.performanceChart ?? []);
      setMonthlyChart(data.monthlyChart ?? []);
      setTopMagazinesChart(data.topMagazinesChart ?? data.topMagazines ?? []);
    } catch (err) {
      console.error("Dashboard fetch failed:", err);
      loadFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackData = () => {
    setStats({
      totalBlogs: 24,
      totalMagazines: 6,
      totalBlogViews: 320,
      totalMagazineViews: 12450,
      totalDownloads: 3890,
      newInPeriod: 4,
    });

    setRecentActivity([
      { type: "magazine", title: "Volume 1, Ed 6", date: "2025-05-19" },
      { type: "blog", title: "Managing Limited Resources", date: "2025-05-19" },
    ]);

    setTopDownloadedMagazine({ title: "Vol 1 Ed 4 – Autism", downloads: 1240 });
    setTopViewedMagazine({ title: "Vol 1 Ed 4", views: 1240 });
    setTopBlog({ title: "Pathways to Well-Being", views: 3420 });

    // Optional: fallback chart data
    setContentChart([
      { name: "Blogs", value: 24 },
      { name: "Magazines", value: 6 },
    ]);
    setPerformanceChart([
      { name: "Views", value: 12770 },
      { name: "Downloads", value: 3890 },
    ]);
  };

  useEffect(() => {
    fetchDashboardData();
  }, [filterType, customMonth, customRange]);

  // ──────────────────────────────────────────────────────────────
  //  UI HELPERS
  // ──────────────────────────────────────────────────────────────

  const getFilterLabel = () => {
    if (filterType === "all") return "All Time";
    if (filterType === "thisMonth") return "This Month";
    if (filterType === "lastMonth") return "Last Month";
    if (filterType === "last30") return "Last 30 Days";
    if (filterType === "custom") {
      if (!startDate || !endDate) return "Custom Range";
      return `${startDate.toLocaleDateString()} – ${endDate.toLocaleDateString()}`;
    }
    return "—";
  };

  const isActive = (type) => filterType === type;

  // ──────────────────────────────────────────────────────────────
  //  RENDER
  // ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
        <Spinner animation="border" variant="primary" style={{ width: "2rem", height: "2rem" }} />
        <h5 className="mt-4 text-muted">Loading dashboard data...</h5>
      </div>
    );
  }

  const statCards = [
    { icon: FiFileText, label: "Total Blogs", value: stats.totalBlogs, color: "#8b5cf6" },
    { icon: FiBookOpen, label: "Total Magazines", value: stats.totalMagazines, color: "#a78bfa" },
    { icon: FiEye, label: "Magazine Views", value: stats.totalMagazineViews.toLocaleString(), color: "#60a5fa" },
    { icon: FiEye, label: "Blog Views", value: stats.totalBlogViews.toLocaleString(), color: "#60a5fa" },
    { icon: FiDownload, label: "Total Downloads", value: stats.totalDownloads.toLocaleString(), color: "#34d399" },
    { icon: FiCalendar, label: "New in Period", value: stats.newInPeriod, color: stats.newInPeriod > 0 ? "#f59e0b" : "#94a3b8" },
  ];

  return (
    <div className="container-fluid py-2">
    <h2 className="mb-4 fw-bold text-dark">Dashboard Overview</h2>
      {/* Filter Bar */}
      <div className="mb-5 card border-0 shadow-sm rounded-4 overflow-hidden">
  {/* Header with gradient */}
  <div 
    className="card-header text-white border-0 py-4"
    style={{
      background: 'linear-gradient(135deg, #4c1d95, #6d28d9)',
    }}
  >
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
      <div>
        <h5 className="mb-1 fw-bold">Date Range Filter</h5>
        <small className="opacity-85">
          {getFilterLabel() || "Choose a period to analyze performance"}
        </small>
      </div>

      <button
        onClick={fetchDashboardData}
        disabled={loading}
        className={`
          btn btn-light btn-sm px-4 py-2 fw-medium rounded-pill 
          d-flex align-items-center gap-2 shadow
          ${loading ? 'opacity-75' : 'hover-shadow-lg'}
        `}
        style={{
          background: 'rgba(255,255,255,0.95)',
          color: '#4c1d95',
        }}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Updating...
          </>
        ) : (
          <>
            <FiCalendar size={16} />
            Apply Filter
          </>
        )}
      </button>
    </div>
  </div>

        <div className="card-body">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            {/* Quick presets */}
            {["thisMonth", "lastMonth", "last30", "all"].map(type => (
              <button
                key={type}
                className={`btn btn-outline-primary btn-sm ${isActive(type) ? "active" : ""}`}
                onClick={() => {
                  setFilterType(type);
                  if (type !== "custom") {
                    setCustomRange([null, null]);
                  }
                }}
              >
                {type === "thisMonth" ? "This Month" :
                 type === "lastMonth" ? "Last Month" :
                 type === "last30" ? "Last 30 Days" : "All Time"}
              </button>
            ))}

            {/* Custom range selector */}
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{ width: "160px" }}
              >
                <option value="custom">Custom Range</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="last30">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>

              {filterType === "custom" && (
                <>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate ? startDate.toISOString().split("T")[0] : ""}
                    onChange={e => setCustomRange([e.target.value ? new Date(e.target.value) : null, endDate])}
                  />
                  <span>–</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate ? endDate.toISOString().split("T")[0] : ""}
                    onChange={e => setCustomRange([startDate, e.target.value ? new Date(e.target.value) : null])}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row g-4 mb-5">
        {statCards.map((card, i) => (
          <div key={i} className="col-md-6 col-lg-4 col-xl-3">
            <div className="card h-100 shadow-sm border-0 hover-lift">
              <div className="card-body d-flex align-items-center gap-4">
                <div className="rounded-circle p-3 text-white" style={{ background: card.color }}>
                  <card.icon size={28} />
                </div>
                <div>
                  <div className="text-muted small">{card.label}</div>
                  <h4 className="mb-0 fw-bold">{card.value}</h4>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="row g-4 mb-5">
        {/* Pie - Content Distribution */}
        <div className="col-lg-4">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-header">Content Distribution</div>
            <div className="card-body" style={{ height: 300 }}>
              {contentChart.length > 0 ? (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={contentChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label fill="#8884d8" />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                  No data
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bar - Performance */}
        <div className="col-lg-4">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-header">Performance Overview</div>
            <div className="card-body" style={{ height: 300 }}>
              {performanceChart.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart data={performanceChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#6d28d9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                  No data
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Line - Monthly Growth */}
        <div className="col-lg-4">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-header">Monthly Growth</div>
            <div className="card-body" style={{ height: 300 }}>
              {monthlyChart.length > 0 ? (
                <ResponsiveContainer>
                  <LineChart data={monthlyChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#4c1d95" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                  No data
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top Magazines Bar Chart */}
      <div className="row g-4 mb-5">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header">Top Viewed Magazines</div>
            <div className="card-body" style={{ height: 340 }}>
              {topMagazinesChart.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart data={topMagazinesChart} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="title" width={220} />
                    <Tooltip />
                    <Bar dataKey="views" fill="#60a5fa" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                  No magazine data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row g-4">
        {/* Recent Activity */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-header bg-white border-0 py-3 px-4 d-flex align-items-center">
              <FiTrendingUp size={20} className="text-purple me-2" />
              <h5 className="mb-0 fw-semibold">Recent Activity</h5>
            </div>
            <div className="card-body p-4">
              {loading ? (
                <p className="text-muted text-center">Loading...</p>
              ) : recentActivity.length === 0 ? (
                <p className="text-muted text-center">No recent activity</p>
              ) : (
                <ul className="list-unstyled mb-0">
                  {recentActivity.map((act, i) => (
                    <li key={i} className="d-flex align-items-start gap-3 py-3 border-bottom last:border-0">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0"
                        style={{
                          width: "40px",
                          height: "40px",
                          background: act.type === "magazine" ? "#a78bfa" : "#8b5cf6",
                        }}
                      >
                        {act.type === "magazine" ? <FiBookOpen size={18} /> : <FiFileText size={18} />}
                      </div>
                      <div className="flex-grow-1">
                        <p className="mb-1 fw-medium text-dark">
                          <span className="fw-semibold">{act.title}</span>
                        </p>
                        <small className="text-muted">{act.date}</small>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-header bg-white border-0 py-3 px-4">
              <h5 className="mb-0 fw-semibold">Most Popular Content</h5>
            </div>
            <div className="card-body p-4">
              {loading ? (
                <p className="text-muted text-center">Loading...</p>
              ) : (
                <>
                  {/* Top Magazine */}
                  <div className="d-flex align-items-center gap-3 mb-4 pb-4 border-bottom">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white"
                      style={{ width: "50px", height: "50px", background: "#34d399" }}
                    >
                      <FiDownload size={22} />
                    </div>
                    <div className="flex-grow-1">
                      <p className="mb-1 text-muted small">Most Downloaded Magazine</p>
                      <h6 className="mb-1 fw-bold text-dark">
                        {topDownloadedMagazine?.title || "N/A"}
                      </h6>
                      <small className="text-success fw-semibold">
                        {topDownloadedMagazine?.downloads?.toLocaleString() || 0} downloads
                      </small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3 mb-4 pb-4 border-bottom">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white"
                      style={{ width: "50px", height: "50px", background: "#60a5fa" }}
                    >
                      <FiEye size={22} />
                    </div>
                    <div className="flex-grow-1">
                      <p className="mb-1 text-muted small">Most Viewed Magazine</p>
                      <h6 className="mb-1 fw-bold text-dark">
                        {topViewedMagazine?.title || "N/A"}
                      </h6>
                      <small className="text-primary fw-semibold">
                        {topViewedMagazine?.views?.toLocaleString() || 0} views
                      </small>
                    </div>
                    </div>

                  {/* Top Blog */}
                  <div className="d-flex align-items-center gap-4">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white"
                      style={{ width: "50px", height: "50px", background: "#60a5fa" }}
                    >
                      <FiEye size={22} />
                    </div>
                    <div className="flex-grow-1">
                      <p className="mb-1 text-muted small">Most Viewed Blog</p>
                      <h6 className="mb-1 fw-bold text-dark">
                        {topBlog?.title || "N/A"}
                      </h6>
                      <small className="text-primary fw-semibold">
                        {topBlog?.views?.toLocaleString() || 0} views
                      </small>
                    </div>
                    </div>
                   
                  
                </>
              )}
            </div>
          </div>
        </div>
     </div>

      {/* Bottom row – Recent + Top performers */}
      {/* ... you can keep your existing Recent Activity + Most Popular Content sections here ... */}
      {/* They already have loading checks inside */}

    </div>
  );
};

export default Dashboard;